import React, { useMemo } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { useDashboard } from '../hooks/useDashboard';
import DashboardAlerts from '../components/DashboardAlerts';
import WorkQueueSection from '../components/WorkQueueSection';
import ReportingProgressPanel from '../components/ReportingProgressPanel';
import ReportingChecklistPanel from '../components/ReportingChecklistPanel';
import OutgoingInvitationsCard from '../components/OutgoingInvitationsCard';
import RecentActivityFeed from '../components/RecentActivityFeed';
import FinanceSnapshot from '../components/FinanceSnapshot';
import DashboardSkeleton from '../components/DashboardSkeleton';
import { QuickActionsBar, ConfigShortcuts } from '../components/QuickActionsBar';
import {
  buildConfigShortcuts,
  buildQuickActions,
  dashboardPermissionActionCodes,
} from '../utils/dashboardLocalActions';
import { invitesPath } from '../utils/dashboardPaths';
import { getScopedActivityEntryCode } from '@features/entity/types/actionCodes';
import './dashboard.css';

const EntityDashboard: React.FC = () => {
  const params = useParams<{
    churchId?: string;
    districtId?: string;
    nationId?: string;
  }>();

  const churchId = Number(params.churchId) || 0;
  const districtId = Number(params.districtId) || 0;
  const nationId = Number(params.nationId) || 0;

  const scope = churchId > 0 ? 'CHURCH' : districtId > 0 ? 'DISTRICT' : 'NATION';
  const scopeId = churchId || districtId || nationId;
  const scopeSegment = getApiScopeFromValue(scope);

  const { data, loading, error, refresh, accessDenied } = useDashboard(scope, scopeId);

  const permissionCodes = useMemo(() => dashboardPermissionActionCodes(scope), [scope]);
  const { permittedActions, loading: permissionsLoading } = usePermissions({
    scopeType: scope,
    scopeId,
    actionCodes: permissionCodes,
  });

  const quickActions = useMemo(
    () => (permissionsLoading ? [] : buildQuickActions(scope, scopeId, permittedActions)),
    [permissionsLoading, scope, scopeId, permittedActions]
  );
  const configShortcuts = useMemo(
    () => (permissionsLoading ? [] : buildConfigShortcuts(scope, scopeId, permittedActions)),
    [permissionsLoading, scope, scopeId, permittedActions]
  );
  const canSaveActivity = useMemo(() => {
    const saveCode = getScopedActivityEntryCode(scope, 'save');
    return Boolean(saveCode && permittedActions.includes(saveCode));
  }, [scope, permittedActions]);

  if (accessDenied) {
    return null;
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="dashboard-page">
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{error ?? 'Unable to load dashboard.'}</span>
          <Button variant="outline-danger" size="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const hasContent = Boolean(
    data.alerts?.length ||
    data.work_queue ||
    data.reporting_checklist?.length ||
    data.reporting_progress?.length ||
    data.invitations ||
    data.recent_activity?.length ||
    data.finance_snapshots?.length ||
    quickActions.length ||
    configShortcuts.length
  );

  return (
    <div className="dashboard-page">
      <header className="dashboard-hero">
        <h1 className="dashboard-hero__title visually-hidden">Dashboard</h1>
      </header>

      {data.alerts && (
        <DashboardAlerts alerts={data.alerts} scope={scope} scopeId={scopeId} />
      )}

      <QuickActionsBar actions={quickActions} />

      {data.work_queue && (
        <WorkQueueSection
          workQueue={data.work_queue}
          scopeSegment={scopeSegment}
          scopeId={scopeId}
        />
      )}

      {data.reporting_checklist && data.reporting_checklist.length > 0 && (
        <ReportingChecklistPanel
          periods={data.reporting_checklist}
          scope={scope}
          scopeId={scopeId}
          canSaveActivity={canSaveActivity}
        />
      )}

      {data.invitations && (
        <OutgoingInvitationsCard
          invitations={data.invitations}
          invitesPath={invitesPath(scope, scopeId)}
        />
      )}

      {data.reporting_progress && (
        <ReportingProgressPanel
          periods={data.reporting_progress}
          scope={scope}
          scopeId={scopeId}
        />
      )}

      {data.finance_snapshots && data.finance_snapshots.length > 0 && (
        <FinanceSnapshot
          periods={data.finance_snapshots}
          scope={scope}
          scopeId={scopeId}
        />
      )}

      {data.recent_activity && <RecentActivityFeed items={data.recent_activity} />}

      <ConfigShortcuts shortcuts={configShortcuts} />

      {!hasContent && (
        <Alert variant="success" className="mb-0">
          All caught up — no outstanding work for your current permissions.
        </Alert>
      )}
    </div>
  );
};

export default EntityDashboard;
